import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../commonComponents/modal/Modal';
import ModalHeader from '../../../commonComponents/modal/ModalHeader';
import FormInput from '../../../commonComponents/forms/FormInput';
import ModalFooter from '../../../commonComponents/modal/ModalFooter';
import OrganizationProp from '../../../commonComponents/propTypes/organizationProp';
import withCSRF from '../../../commonComponents/withCSRF';
import translate from '../../../commonComponents/translate';
import { compose } from '../../../services/utilityFunctions';

const LostReasonModal = ({ t, onClose, isLoading, organization }) => {
  const { lostReasons = [] } = organization?.settings?.opportunities || {};

  const [lostReason, setLostReason] = useState('');
  const [validationRules, setValidationRules] = useState({
    lostReason: {
      required: true,
      isTouched: false,
      showError: false,
    },
  });

  const items = useMemo(
    () =>
      lostReasons.map(l => ({
        id: l.name,
        text: l.name,
      })),
    [lostReasons],
  );

  const handleChange = useCallback(
    value => {
      setValidationRules(prevRules => ({
        ...prevRules,
        lostReason: {
          ...prevRules?.lostReason,
          isTouched: true,
          showError: false,
        },
      }));
      setLostReason(value);
    },
    [setLostReason, setValidationRules],
  );

  const handleClose = useCallback(
    isToSave => {
      if (
        isToSave &&
        validationRules?.lostReason?.required &&
        (!validationRules?.lostReason?.isTouched ||
          validationRules?.lostReason?.showError)
      ) {
        setValidationRules(prevRules => ({
          ...prevRules,
          lostReason: {
            ...prevRules?.lostReason,
            showError: true,
          },
        }));
        return;
      }

      if (isToSave) {
        const lostReasonToSave = lostReasons.find(l => l.name === lostReason);
        delete lostReasonToSave.position;
        onClose(true, lostReasonToSave);
        return;
      }
      onClose(false);
    },
    [onClose, validationRules, lostReasons, lostReason],
  );

  return (
    <Modal
      id="lost-reason-modal"
      onClose={() => handleClose(false)}
      tabIndex={0}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg market-modal-remove"
        role="document"
      >
        <div
          className="modal-content"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <ModalHeader
            title={t('opportunity.lostReason')}
            onClose={() => handleClose(false)}
          />
          <div className="modal-body">
            <FormInput
              id="lostReason"
              name="lostReason"
              value={lostReason}
              onChange={handleChange}
              label=""
              containerClassName="col-12"
              type="select"
              placeholder={t('opportunity.lostReason')}
              groupProps={{ className: 'row' }}
              inputProps={{
                width: '100%',
                multiple: false,
                items,
              }}
            />
            {validationRules?.lostReason?.showError && (
              <span className="has-error" style={{ float: 'left' }}>
                {t('ERROR_FIELD_REQUIRED')}
              </span>
            )}
          </div>
          <ModalFooter
            saveIsLoading={isLoading}
            disabledSave={isLoading}
            onCancel={() => handleClose(false)}
            onSave={() => handleClose(true, lostReason)}
            cancelText={t('cancel')}
            saveText={t('save')}
          />
        </div>
      </div>
    </Modal>
  );
};

LostReasonModal.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  organization: PropTypes.shape(OrganizationProp.propType).isRequired,
};

LostReasonModal.defaultProps = {
  isLoading: false,
};

export default compose(withCSRF, translate)(LostReasonModal);
